import React, { useId, useLayoutEffect, useRef } from "react";
import { LuTrash2 } from "react-icons/lu";
import { cn } from "../../components/ui";

interface OrderFieldProps {
  number: number;
  label: React.ReactNode;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  hint?: string;
  rows?: number;
  disabled?: boolean;
  /** Microfone dentro do campo (DictationButton). */
  dictation?: React.ReactNode;
  onRemove?: () => void;
  removeLabel?: string;
}

// Campo numerado do Novo pedido: o número fica azul quando preenchido e o texto cresce com o conteúdo.
export const OrderField: React.FC<OrderFieldProps> = ({
  number, label, value, onChange, placeholder, hint, rows = 1, disabled, dictation, onRemove, removeLabel,
}) => {
  const id = useId();
  const ref = useRef<HTMLTextAreaElement>(null);
  const filled = !!value.trim();

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = "auto";
    // Vazio, mede pelo placeholder: senão as linhas dele ficam escondidas.
    if (!value && placeholder) el.value = placeholder;
    el.style.height = `${el.scrollHeight}px`;
    el.value = value;
  }, [value, placeholder]);

  return (
    <div
      className={cn(
        "group flex gap-3.5 rounded-panel border border-primary-line bg-primary-tint p-4 transition-colors duration-base",
        "hover:border-primary focus-within:border-primary focus-within:bg-surface focus-within:shadow-focus"
      )}
    >
      <span
        className={cn(
          "grid h-8 w-8 shrink-0 place-items-center rounded-full border-strong text-label font-bold transition-colors duration-base",
          filled ? "border-primary bg-primary text-on-primary" : "border-primary-line bg-surface text-primary"
        )}
        aria-hidden
      >
        {number}
      </span>
      <div className="flex min-w-0 flex-1 flex-col gap-1.5">
        <div className="flex min-h-8 items-center justify-between gap-2">
          <label htmlFor={id} className={cn("font-semibold group-focus-within:text-primary", filled ? "text-primary" : "text-fg-secondary")}>
            {label}
          </label>
          {onRemove && (
            <button
              type="button"
              onClick={onRemove}
              aria-label={removeLabel}
              className="-my-2 -mr-2 grid h-11 w-11 place-items-center rounded-full text-fg-subtle hover:bg-danger-soft hover:text-danger focus-visible:outline-none focus-visible:shadow-focus"
            >
              <LuTrash2 size={18} aria-hidden />
            </button>
          )}
        </div>
        <div className="relative">
          <textarea
            id={id}
            ref={ref}
            rows={rows}
            value={value}
            disabled={disabled}
            placeholder={placeholder}
            autoComplete="off"
            onChange={(e) => onChange(e.target.value)}
            className={cn(
              "block min-h-10 w-full resize-none overflow-hidden border-0 border-b-2 border-line bg-transparent py-2 leading-normal outline-none transition-colors duration-base placeholder:text-fg-subtle focus:border-primary",
              dictation ? "pr-12" : "pr-0"
            )}
          />
          {dictation}
        </div>
        {hint && <p className="text-caption text-fg-subtle">{hint}</p>}
      </div>
    </div>
  );
};
